import React, { Fragment, useEffect, useState, useMemo } from 'react'
import { LoadingSpinner, AggregatedMetricLineChart } from '../components'
import { Area, DashboardMetaProps, DashboardStats, Event, Shift } from '../data/types'
import { Card } from '../components/ui/Card'
import { API_BASE_URL } from '../data'

// New Type for Breakdown Rows
type BreakdownItem = {
    id: number;
    name: string;
    accepted: number;
    declined: number;
    pending: number;
}

// Extended Stats Type
// We make the original DashboardStats fields optional since this specific API endpoint 
// only returns the breakdown array, not the full stats object.
type ExtendedDashboardStats = Partial<DashboardStats> & {
    breakdown: BreakdownItem[];
}

type Props = {
    meta: DashboardMetaProps,
    event?: Event
    area?: Area
    shift?: Shift
}

export const Dashboard = ({ meta, event, area, shift }: Props) => {
    const [stats, setStats] = useState<ExtendedDashboardStats | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const buildQueryString = () => {
        const params = new URLSearchParams()
        if (event) params.append('eventId', event?.id.toString())
        if (area) params.append('areaId', area?.id.toString())
        if (shift) params.append('shiftId', shift?.id.toString());
        return params.toString()
    }

    useEffect(() => {
        const fetchGroupStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const queryString = buildQueryString();
                const response = await fetch(`${API_BASE_URL}/dashboard/stats/allocation-breakdown?${queryString}`, { credentials: 'include' })
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

                const result = await response.json()

                // FIX: The API returns an array, so we wrap it in an object to match our state shape
                setStats({
                    breakdown: Array.isArray(result) ? result : []
                });

            } catch (err) {
                setError(err instanceof Error ? err.message : "Error while fetching stats.")
            }
            finally {
                setLoading(false);
            }
        }
        fetchGroupStats();
    }, [event, area, shift])

    // Calculate Totals for Footer Row
    const totals = useMemo(() => {
        if (!stats?.breakdown) return { accepted: 0, declined: 0, pending: 0 };
        return stats.breakdown.reduce((acc, curr) => ({
            accepted: acc.accepted + Number(curr.accepted),
            declined: acc.declined + Number(curr.declined),
            pending: acc.pending + Number(curr.pending),
        }), { accepted: 0, declined: 0, pending: 0 });
    }, [stats]);

    const getPercent = (val: number, totalA: number, totalR: number, totalP: number) => {
        const total = Number(totalA) + Number(totalR) + Number(totalP);
        if (total === 0) return 0;
        return Math.round((Number(val) / total) * 100);
    }

    // Determine First Column Header
    const getColumnLabel = () => {
        if (shift) return "Volunteer";
        if (area) return "Shift";
        if (event) return "Area";
        return "Event";
    }

    return (
        <Fragment>
            <div className="flex-1 container mx-auto pb-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        {meta.title}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        {meta.description}
                    </p>
                </div>

                {loading && <LoadingSpinner meta={{ loadingText: meta.loadingText }} />}

                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">{meta.errorTitle}</h3>
                        <p className="text-red-600 dark:text-red-300">{error}</p>
                    </div>
                )}

                {!loading && !error && stats && (
                    <div className="space-y-8">

                        {!event && !area && !shift && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <AggregatedMetricLineChart
                                    metricKeys={['registered_users_iiuk_terms_accepted', 'registered_users_iiuk_terms_pending', 'registered_users_non_iiuk_terms_accepted', 'registered_users_non_iiuk_terms_pending']}
                                    title="Unique Sign-Ins"
                                    description="Volunteers who have signed in to VMS"
                                    days={3}
                                    aggregation="none"
                                    lineColor="#8b5cf6"
                                    height={200}
                                />
                                <AggregatedMetricLineChart
                                    metricKeys={['registered_users_iiuk_terms_accepted', 'registered_users_non_iiuk_terms_accepted']}
                                    title="Agreed to Terms"
                                    description="Volunteers who have agreed to the terms"
                                    days={3}
                                    aggregation="none"
                                    lineColor="#3b82f6"
                                    height={200}
                                />
                                <AggregatedMetricLineChart
                                    metricKeys={['submitted_profiles_local', 'submitted_profiles_visitor']}
                                    title="Profile submissions"
                                    description="Volunteers who have completed their profiles"
                                    days={3}
                                    aggregation="none"
                                    lineColor="#10b981"
                                    height={200}
                                />
                            </div>
                        )}

                        {stats.breakdown && stats.breakdown.length > 0 ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                                {/* Table 1: Raw Counts */}
                                <div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-primary-600 text-white">
                                            <tr>
                                                <th scope="col" className="py-3.5 ps-4 pe-3 text-start text-sm font-semibold sm:ps-6">{getColumnLabel()}</th>
                                                <th scope="col" className="px-3 py-3.5 text-end text-sm font-semibold">Total</th>
                                                <th scope="col" className="px-3 py-3.5 text-end text-sm font-semibold">Accepted</th>
                                                <th scope="col" className="px-3 py-3.5 text-end text-sm font-semibold">Rejected</th>
                                                <th scope="col" className="px-3 py-3.5 text-end text-sm font-semibold">Pending</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-800 dark:divide-gray-700">
                                            {stats.breakdown.map((row) => (
                                                <tr key={row.id}>
                                                    <td className="whitespace-nowrap py-3 ps-4 pe-3 text-sm font-bold text-gray-900 dark:text-gray-100 sm:ps-6">{row.name}</td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">{Number(row.accepted) + Number(row.declined) + Number(row.pending)}</td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">{row.accepted}</td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">{row.declined}</td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">{row.pending}</td>
                                                </tr>
                                            ))}
                                            <tr className="bg-primary-600/90 text-white italic">
                                                <td className="whitespace-nowrap py-3 ps-4 pe-3 text-sm font-bold sm:ps-6">Overall</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">{Number(totals.accepted) + Number(totals.declined) + Number(totals.pending)}</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">{totals.accepted}</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">{totals.declined}</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">{totals.pending}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Table 2: Percentages */}
                                <div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-primary-600 text-white">
                                            <tr>
                                                <th scope="col" className="py-3.5 ps-4 pe-3 text-start text-sm font-semibold sm:ps-6">{getColumnLabel()}</th>
                                                <th scope="col" className="px-3 py-3.5 text-end text-sm font-semibold">Accepted %</th>
                                                <th scope="col" className="px-3 py-3.5 text-end text-sm font-semibold">Rejected %</th>
                                                <th scope="col" className="px-3 py-3.5 text-end text-sm font-semibold">Pending %</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white dark:bg-gray-800 dark:divide-gray-700">
                                            {stats.breakdown.map((row) => (
                                                <tr key={row.id}>
                                                    <td className="whitespace-nowrap py-3 ps-4 pe-3 text-sm font-bold text-gray-900 dark:text-gray-100 sm:ps-6">{row.name}</td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">
                                                        {getPercent(row.accepted, row.accepted, row.declined, row.pending)}%
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">
                                                        {getPercent(row.declined, row.accepted, row.declined, row.pending)}%
                                                    </td>
                                                    <td className="whitespace-nowrap px-3 py-3 text-sm text-end text-gray-900 dark:text-gray-300">
                                                        {getPercent(row.pending, row.accepted, row.declined, row.pending)}%
                                                    </td>
                                                </tr>
                                            ))}
                                            {/* Overall Row */}
                                            <tr className="bg-primary-600/90 text-white italic">
                                                <td className="whitespace-nowrap py-3 ps-4 pe-3 text-sm font-bold sm:ps-6">Overall</td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">
                                                    {getPercent(totals.accepted, totals.accepted, totals.declined, totals.pending)}%
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">
                                                    {getPercent(totals.declined, totals.accepted, totals.declined, totals.pending)}%
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-sm text-end font-bold">
                                                    {getPercent(totals.pending, totals.accepted, totals.declined, totals.pending)}%
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="p-8 text-center bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <p className="text-gray-500">No allocation data available for this selection.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Fragment>
    )
}